import React, { useMemo, useState } from "react";
import Table from "../../components/Table";
import TitleButtons from "../../components/TitleButtons";
import { generatePdfReport } from "../../utils/pdf/generatePdfReport";
import numeroParaExtenso from "../../utils/numeroPorExtenso";
import { useEstoqueDisponivel } from "../Suprimentos/Estoque/estoqueData";
import { useNavigate } from "react-router-dom";

const SuprimentosEstoque: React.FC = () => {
  const navigate = useNavigate();
  const { itens, loading } = useEstoqueDisponivel();

  const headers = [
    "Posição",
    "Código",
    "NCM",
    "Nome do Produto",
    "Custo Unitário",
    "Moeda",
    "Quantidade",
    "Classificação",
  ];

  const columnWidths = [
    "10px",
    "70px",
    "70px",
    "350px",
    "100px",
    "60px",
    "10px",
    "50px",
  ];

  const [selectedColumns, setSelectedColumns] = useState<boolean[]>(
    headers.map(() => true)
  );

  const enabledColumnIndexes = selectedColumns
    .map((checked, i) => (checked ? i : -1))
    .filter((i) => i !== -1);

  /** ------------------ formatação por moeda ------------------ */
  const formatCurrency = (
    valor: string | number | null | undefined,
    moeda?: string | null
  ): string => {
    if (!moeda) return "N/A";
    const n = typeof valor === "string" ? Number(valor) : valor ?? 0;
    if (!isFinite(n)) return "N/A";

    const symbol =
      moeda === "USD"
        ? "$"
        : moeda === "EUR"
        ? "€"
        : moeda === "BRL"
        ? "R$"
        : "";

    if (!symbol) return "N/A";

    return `${symbol} ${n.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const moedaFromItem = (p: any) =>
    p?.moeda && String(p.moeda).trim() !== "" ? String(p.moeda) : null;

  // linhas da tabela
  const rows = useMemo(
    () =>
      itens.map((p) => {
        const moeda = moedaFromItem(p);
        return [
          p.posicao ?? "",
          p.codigo ?? "",
          p.ncm ?? "",
          p.nomeProduto ?? "",
          formatCurrency(p.custoUnitario, moeda), // custo com símbolo da moeda ou N/A
          moeda ?? "N/A", // coluna Moeda
          String(p.quantidade ?? 0),
          p.classificacao ?? "",
        ];
      }),
    [itens]
  );

  const totalProdutos = useMemo(
    () => itens.reduce((acc, p) => acc + (p.quantidade ?? 0), 0),
    [itens]
  );

  const valorTotal = useMemo(
    () =>
      itens.reduce((acc, p) => {
        const custo = Number(p.custoUnitario ?? 0);
        const qtd = p.quantidade ?? 0;
        return acc + (isFinite(custo * qtd) ? custo * qtd : 0);
      }, 0),
    [itens]
  );

  const codigoIdx = headers.indexOf("Código");

  return (
    <div>
      <TitleButtons
        title={"Solicitações"}
        buttons={[
          {
            label: "Gerar Relatório",
            onClick: () =>
              generatePdfReport({
                reportTitle: "Relatório de Estoque - Suprimentos",
                fileNamePrefix: "Suprimentos-Estoque",
                subtitle: "Informações dos Produtos no Estoque",
                headers,
                rows,
                custoColIndex: 4,
                enabledColumns: enabledColumnIndexes,
                footerLines: [
                  `Total de Produtos no Estoque: ${totalProdutos}`,
                  `Valor Total em Estoque: R$ ${valorTotal.toLocaleString(
                    "pt-BR",
                    { minimumFractionDigits: 2 }
                  )} (${numeroParaExtenso(Math.floor(valorTotal))} reais)`,
                ],
              }),
            variant: "secondary",
            dropdownOptions: headers.map((h, i) => ({
              label: h,
              checked: selectedColumns[i],
            })),
            onOptionChange: (index, checked) => {
              const updated = [...selectedColumns];
              updated[index] = checked;
              setSelectedColumns(updated);
            },
          },
          {
            label: "Realizar Movimentação",
            onClick: () =>
              navigate("/suprimentos/estoque/realizar-movimentacao"),
            variant: "primary",
          },
        ]}
      />

      {loading ? (
        <div style={{ padding: 16 }}>Carregando estoque…</div>
      ) : (
        <Table
          headers={headers}
          rows={rows}
          columnWidths={columnWidths}
          onDoubleClick={(_viewIndex, rowData) => {
            const codigo = String(rowData[codigoIdx] ?? "");
            if (codigo) {
              navigate(
                `/suprimentos/produtos/${encodeURIComponent(codigo)}/historico`
              );
            }
          }}
          responsiveWidths={{
            1300: {
              1: "10px",
              2: "70px",
              3: "0px",
              4: "350px",
              5: "100px",
              6: "60px",
              7: "10px",
              8: "50px",
            },
          }}
        />
      )}
    </div>
  );
};

export default SuprimentosEstoque;
